import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { SecurityService } from '@wsphere/staff/web';
import { Warranty } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map, switchMap } from 'rxjs';
import { WarrantyWebService } from '../../../warranty/services';
import { formatDateLocalYYYYMMDD } from '../../../warranty/utils/date.utils';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-warranty-policy-card',
  templateUrl: './warranty-policy-card.component.html',
  standalone: false,
})
export class WarrantyPolicyCardComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private securitySvc: SecurityService,
    private warrantySvc: WarrantyWebService,
    private toastSvc: ToastService,
    private clipboard: Clipboard
  ) {}

  @Input() warranty?: Warranty.Model;

  overlayOpen = false;

  draft = Warranty.Status.DRAFT;
  updatedWarranty$ = new BehaviorSubject<Warranty.Model | null>(null);
  @ViewChild('termStartReasonDialog') termStartReasonDialog?: DialogComponent;
  termStartReasonControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  termStartReasonSaving = false;
  termStartRevertSaving = false;
  termStartAuditContext: { warrantyId: string; previousTermStart: Date | null; updatedTermStart: Date } | null = null;

  warranty$ = combineLatest([this.route.data, this.updatedWarranty$]).pipe(
    map(([data, warranty]) => warranty ?? (data['warranty'] as Warranty.Model))
  );

  canAdjust$ = this.warranty$.pipe(
    switchMap(warranty => {
      return this.securitySvc.hasAccess(
        'wrt_AdjustWarranty',
        warranty.distributor?.permissionKey ? [warranty.distributor.permissionKey] : null
      );
    })
  );

  toggle() {
    this.overlayOpen = !this.overlayOpen;
  }

  updateTermStart(warrantyModel: Warranty.Model, date: Date) {
    if (!this.warranty) return;

    const previousTermStart = warrantyModel.termStart ? new Date(warrantyModel.termStart) : null;
    this.termStartAuditContext = {
      warrantyId: this.warranty.id,
      previousTermStart,
      updatedTermStart: date,
    };
    this.termStartReasonControl.reset('');
    this.termStartReasonDialog?.open();
  }

  handleTermStartDialogClosed() {
    if (this.termStartReasonSaving) return;
    this.termStartAuditContext = null;
    this.termStartReasonControl.reset('');
  }

  async submitTermStartReason() {
    if (!this.termStartAuditContext) return;

    this.termStartReasonControl.markAsTouched();
    if (this.termStartReasonControl.invalid) return;

    this.termStartReasonSaving = true;
    try {
      const context = this.termStartAuditContext;
      const updated = await this.warrantySvc
        .update(this.termStartAuditContext.warrantyId, { termStart: this.termStartAuditContext.updatedTermStart })
        .catch(({ error }) => {
          this.toastSvc.show({
            type: 'error',
            title: 'Something went wrong',
            text: error.message,
          });
        });

      const updatedWarranty = updated && typeof updated === 'object' ? (updated as { id: string }) : undefined;
      if (!updatedWarranty?.id) return;
      const formattedTermStart = formatDateLocalYYYYMMDD(this.termStartAuditContext.updatedTermStart);
      const formattedPreviousStart = formatDateLocalYYYYMMDD(this.termStartAuditContext.previousTermStart);
      await this.warrantySvc.logTermStartAdjustmentReason(this.termStartAuditContext.warrantyId, {
        reason: this.termStartReasonControl.value,
        previousTermStart: formattedPreviousStart
          ? formattedPreviousStart
          : this.termStartAuditContext.previousTermStart,
        updatedTermStart: formattedTermStart ? formattedTermStart : this.termStartAuditContext.updatedTermStart,
      });
      this.termStartReasonDialog?.close();
      if (context) {
        this.toastSvc.show({
          type: 'success',
          title: 'Warranty term start updated',
          text: `Successfully updated warranty term start date to ${context.updatedTermStart.toDateString()} and the expiry date has been adjusted accordingly.`,
        });
        const warrantyId = updatedWarranty.id;
        const refreshedWarranty = await this.warrantySvc.getOneById(warrantyId).catch(({ error }) => {
          this.toastSvc.show({
            type: 'error',
            title: 'Something went wrong',
            text: error.message,
          });
        });

        if (refreshedWarranty) this.warranty = refreshedWarranty;
        await new Promise(res => setTimeout(res, 1000));
        window.location.reload();
      }
      this.termStartAuditContext = null;
    } catch (err) {
      const errorMessage =
        (err as { error?: { message?: string } })?.error?.message ??
        (err instanceof Error ? err.message : 'Unknown error');
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to save reason',
        text: errorMessage,
      });
    } finally {
      this.termStartReasonSaving = false;
    }
  }

  navigateToPolicy(policyId: string | undefined) {
    this.router.navigate(['/policies', policyId]);
  }

  copyReferenceId(referenceId?: string | null, event?: Event) {
    event?.stopImmediatePropagation();
    if (!referenceId) return;
    this.clipboard.copy(referenceId);
    this.toastSvc.show({
      type: 'success',
      title: 'Copied!',
      text: 'Warranty Ref ID copied to clipboard',
    });
  }
}
